import { useSiteInfo } from '../../SiteInfo';

function shouldUseCurrencySymbol(currency: string) {
  return currency === 'EUR';
}

type Price = {
  amount: number;
  currency: string;
};

type ExchangeConfig = {
  selectedCurrency: string;
  inputCurrencies: string[];
  exchangeRates: Record<string, number>;
};

function createPriceFn({
  selectedCurrency,
  inputCurrencies,
  exchangeRates,
}: ExchangeConfig) {
  return (
    price: Price,
    locale?: string,
    opts?: Pick<
      Intl.NumberFormatOptions,
      'minimumFractionDigits' | 'maximumFractionDigits'
    >,
  ) => {
    let targetAmount = price.amount;
    let targetCurrency = price.currency;
    if (
      price.currency !== selectedCurrency &&
      inputCurrencies.includes(price.currency) &&
      exchangeRates[price.currency] &&
      exchangeRates[selectedCurrency] !== undefined
    ) {
      targetAmount =
        (price.amount / exchangeRates[price.currency]) *
        exchangeRates[selectedCurrency];
      targetCurrency = selectedCurrency;
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: targetCurrency,
      currencyDisplay: shouldUseCurrencySymbol(targetCurrency)
        ? 'symbol'
        : 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...(opts || {}),
    }).format(targetAmount);
  };
}

type PriceFn = ReturnType<typeof createPriceFn>;

export function usePriceFn() {
  const {
    selectedCurrency,
    settings: { inputCurrencies, exchangeRates },
  } = useSiteInfo();

  return createPriceFn({
    selectedCurrency,
    inputCurrencies,
    exchangeRates,
  });
}

export function formatLocalPrice(
  priceFn: PriceFn,
  localPrice: Price,
  price: Price,
) {
  const formattedLocal = priceFn(localPrice);
  const formattedPrice = priceFn(price);

  if (!formattedLocal || formattedLocal === formattedPrice) {
    return formattedPrice;
  }

  if (!formattedPrice) {
    return formattedLocal;
  }

  return `${formattedLocal} (≈${formattedPrice})`;
}

export function usePrice(...args: Parameters<PriceFn>) {
  const priceFn = usePriceFn();
  return priceFn(...args);
}

export function getCurrencySymbol(currency: string, locale?: string) {
  if (!shouldUseCurrencySymbol(currency)) {
    return currency;
  }

  return (
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value ?? currency
  );
}

export function getCurrencyName(currency: string, locale: string) {
  const result = new Intl.DisplayNames([locale], { type: 'currency' }).of(
    currency,
  );

  if (result) {
    return `${result} – ${getCurrencySymbol(currency)}`;
  }

  return getCurrencySymbol(currency);
}
